import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, CheckCheck, Loader2, AlertTriangle, Info } from 'lucide-react';

interface BulkApprovalModalProps {
  requestCount: number;
  selectedRequestIds: number[];
  onClose: () => void;
  onConfirm: (selectedRequestIds: number[], comments: string) => Promise<void>;
}

const BulkApprovalModal: React.FC<BulkApprovalModalProps> = ({
  requestCount,
  selectedRequestIds,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(['leaves', 'common', 'translation']);
  const [comments, setComments] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm(selectedRequestIds, comments);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-md w-full" data-testid="bulk-approval-modal">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCheck size={20} className="text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('translation:bulkApproval', 'Bulk Approval')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('Approve')} {requestCount} {t('translation:selectedRequests', 'selected requests')}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="leave-bulk-close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Confirmation */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-3 rtl:space-x-reverse">
              <Info size={20} className="text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800 mb-1">{t('translation:bulkApprovalConfirmation', 'Bulk Approval Confirmation')}</h4>
                <p className="text-sm text-green-700">
                  {t('translation:bulkApprovalMessage', "You're about to approve {{count}} leave requests. This will send notifications to all affected employees.", { count: requestCount })}
                </p>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Manager Comments')} ({t('translation:optional', 'Optional')})
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={t('translation:bulkCommentsPlaceholder', 'Add any comments for the approved requests...')}
              className="w-full h-24 px-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              data-testid="leave-bulk-comments"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {t('translation:bulkCommentsNote', 'This comment will be added to all selected requests.')}
            </p>
          </div>

          {/* Warning */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start space-x-3 rtl:space-x-reverse">
              <AlertTriangle size={20} className="text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800 mb-1">{t('translation:reviewConflicts', 'Review Conflicts')}</h4>
                <p className="text-sm text-orange-700">
                  {t('translation:reviewConflictsMessage', "Please ensure you've reviewed any conflicts before approving multiple requests.")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex items-center justify-end space-x-3 rtl:space-x-reverse">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
              data-testid="leave-bulk-cancel"
            >
              {t('translation:cancel', 'Cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isProcessing}
              data-testid="bulk-confirm-btn"
              className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg transition-colors flex items-center"
            >
              {isProcessing ? (
                <><Loader2 size={16} className="ltr:mr-2 rtl:ml-2 animate-spin" />{t('translation:processing', 'Processing...')}</>
              ) : (
                <><Check size={16} className="ltr:mr-2 rtl:ml-2" />{t('Approve')} ({requestCount})</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkApprovalModal;
